import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ChevronDown, Trash2, Plus, FileText } from "lucide-react";

const HEADER_FIELDS = [
  { key: "delivery_note_id", label: "Delivery Note ID (#)" },
  { key: "document_id", label: "Documento SAP" },
  { key: "ragione_sociale", label: "Cliente / Ragione sociale" },
  { key: "nazione", label: "Nazione" },
  { key: "via", label: "VIA" },
  { key: "citta", label: "Città" },
  { key: "cap", label: "CAP" },
  { key: "provincia", label: "Prov" },
];

export const NoteEditor = ({ note, index, onChange, onRemove }) => {
  const [open, setOpen] = useState(true);

  const setHeader = (key, value) => onChange({ ...note, header: { ...note.header, [key]: value } });
  const setItem = (idx, key, value) => {
    const items = [...note.items];
    items[idx] = { ...items[idx], [key]: value };
    onChange({ ...note, items });
  };
  const removeItem = (idx) => onChange({ ...note, items: note.items.filter((_, i) => i !== idx) });
  const addItem = () =>
    onChange({ ...note, items: [...note.items, { numero_riga: null, codice_articolo: "", lotto: "", pezzi: null }] });

  return (
    <div data-testid={`note-card-${index}`} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
      <div className="flex items-center gap-3 p-4 border-b border-slate-100 dark:border-slate-800">
        <button
          data-testid={`note-toggle-${index}`}
          onClick={() => setOpen((o) => !o)}
          className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
        <div className="h-9 w-9 rounded-lg bg-blue-50 dark:bg-blue-950/40 grid place-items-center shrink-0">
          <FileText className="h-4 w-4 text-blue-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
            {note.header?.ragione_sociale || note.filename}
            <span className="ml-2 text-xs font-normal text-slate-400">
              DN {note.header?.delivery_note_id || "—"} · SAP {note.header?.document_id || "—"} · {note.header?.nazione || "—"}
            </span>
          </div>
          <div className="text-xs text-slate-400 truncate">{note.filename} · {note.items?.length || 0} articoli</div>
        </div>
        <Button
          data-testid={`remove-note-${index}`}
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="text-slate-400 hover:text-rose-600 gap-1.5"
        >
          <Trash2 className="h-4 w-4" /> Rimuovi
        </Button>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-5 space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {HEADER_FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label className="text-xs text-slate-500">{f.label}</Label>
                    <Input
                      data-testid={`note-${index}-header-${f.key}-input`}
                      value={note.header?.[f.key] ?? ""}
                      onChange={(e) => setHeader(f.key, e.target.value)}
                      className="h-9 font-mono-data text-sm"
                    />
                  </div>
                ))}
                <div className="space-y-1.5 col-span-2 sm:col-span-4">
                  <Label className="text-xs text-slate-500">Note Testata</Label>
                  <Textarea
                    data-testid={`note-${index}-header-note_testata-input`}
                    value={note.header?.note_testata ?? ""}
                    onChange={(e) => setHeader("note_testata", e.target.value)}
                    rows={2}
                    className="text-sm"
                  />
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-800/40">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Articoli ({note.items?.length || 0})
                  </span>
                  <Button data-testid={`note-${index}-add-item-button`} size="sm" variant="ghost" onClick={addItem} className="h-7 gap-1 text-blue-600">
                    <Plus className="h-3.5 w-3.5" /> Riga
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200 dark:border-slate-800">
                        <th className="px-4 py-2 w-28">Numero riga</th>
                        <th className="px-4 py-2">Codice Articolo</th>
                        <th className="px-4 py-2">Lotto</th>
                        <th className="px-4 py-2 w-24">Pezzi</th>
                        <th className="px-4 py-2 w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(note.items || []).map((it, idx) => (
                        <tr key={idx} className="border-b border-slate-100 dark:border-slate-800/50">
                          <td className="px-4 py-1.5">
                            <Input data-testid={`note-${index}-edit-row-numero-${idx}`} value={it.numero_riga ?? ""} onChange={(e) => setItem(idx, "numero_riga", e.target.value)} className="h-8 font-mono-data" />
                          </td>
                          <td className="px-4 py-1.5">
                            <Input data-testid={`note-${index}-edit-row-codice-articolo-${idx}`} value={it.codice_articolo ?? ""} onChange={(e) => setItem(idx, "codice_articolo", e.target.value)} className="h-8 font-mono-data" />
                          </td>
                          <td className="px-4 py-1.5">
                            <Input data-testid={`note-${index}-edit-row-lotto-${idx}`} value={it.lotto ?? ""} onChange={(e) => setItem(idx, "lotto", e.target.value)} className="h-8 font-mono-data" />
                          </td>
                          <td className="px-4 py-1.5">
                            <Input data-testid={`note-${index}-edit-row-pezzi-${idx}`} value={it.pezzi ?? ""} onChange={(e) => setItem(idx, "pezzi", e.target.value)} className="h-8 font-mono-data" />
                          </td>
                          <td className="px-4 py-1.5 text-right">
                            <button data-testid={`note-${index}-delete-row-${idx}`} onClick={() => removeItem(idx)} className="text-slate-400 hover:text-rose-600">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
